import React from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ColumnSelectModal from "../../modals/ColumnSelectModal/ColumnSelectModal";
import DateRuleSection from "./components/DateRuleSection";
import ConditionRuleSection from "./components/ConditionRuleSection";
import { useSheetColumns } from "../../../hooks/useSheetColumns";

const NO_VALUE_OPERATORS = ["is null", "is not null"];

export function StepSendRules({
  data,
  updateData,
  setIsValid,
  showErrors,
  campaignType,
  audienceId
}) {
  const theme = useTheme();

  const {
    businessDaysOnly = false,
    dueDateColumnId = null,
    daysOffset = "",
    reference = "",
    ruleColumnId = null,
    operator = "",
    value = "",
  } = data;

  const isSingle = campaignType === "single";
  const isRecurring = campaignType === "recurring";

  // ==============================
  // Buscar colunas reais
  // ==============================
  const {
    columns: columnOptions = [],
    loading: loadingColumns,
    error: columnsError,
    refetch: refetchColumns,
  } = useSheetColumns(audienceId);

  const safeColumnOptions = audienceId ? columnOptions : [];

  const selectedDueDateColumn =
    safeColumnOptions.find((c) => c.id === dueDateColumnId) || null;

  const selectedRuleColumn =
    safeColumnOptions.find((c) => c.id === ruleColumnId) || null;

  const hasDueDateColumn = Boolean(dueDateColumnId);
  const hasRuleColumn = Boolean(ruleColumnId);

  // ==============================
  // Modal
  // ==============================
  const [openModal, setOpenModal] = React.useState(false);
  const [modalField, setModalField] = React.useState(null);

  const openColumnModal = (field) => {
    // 🔒 não deixa abrir se não tiver listagem
    if (!audienceId) return;

    setModalField(field);
    setOpenModal(true);
  };

  const handleSelectColumn = (column) => {
    if (!column) return;

    if (modalField === "dueDate") {
      updateData({ dueDateColumnId: column.id });
    }

    if (modalField === "ruleColumn") {
      updateData({ ruleColumnId: column.id });
    }

    setOpenModal(false);
    setModalField(null);
  };

  // ==============================
  // Validação DATA
  // ==============================
  const dateNeedsDays = reference === "before" || reference === "after";

  const dateIsComplete = hasDueDateColumn
    ? Boolean(reference) && (!dateNeedsDays || Boolean(daysOffset))
    : false;

  const dateIsIncomplete = hasDueDateColumn && !dateIsComplete;

  // ==============================
  // Validação CONDIÇÃO
  // ==============================
  const operatorNeedsValue =
    operator && !NO_VALUE_OPERATORS.includes(operator);

  const conditionIsComplete = hasRuleColumn
    ? Boolean(operator) && (!operatorNeedsValue || Boolean(value?.trim()))
    : false;

  const conditionIsIncomplete = hasRuleColumn && !conditionIsComplete;

  // ==============================
  // REGRA FINAL
  // ==============================
  const hasIncompleteFilter = dateIsIncomplete || conditionIsIncomplete;

  const stepIsValid = isRecurring
    ? dateIsComplete && !hasIncompleteFilter
    : !hasIncompleteFilter;

  React.useEffect(() => {
    setIsValid(stepIsValid);
  }, [stepIsValid, setIsValid]);

  const dateRequiredError = showErrors && isRecurring && !dateIsComplete;

  // ==============================
  // Styles
  // ==============================
  const buttonSx = (isSelected, hasError) => ({
    textTransform: "none",
    borderRadius: 2,
    height: 56,
    px: 2.5,
    border: hasError ? `1px solid ${theme.palette.error.main}` : "none",
    ...(isSelected
      ? {
          backgroundColor: theme.palette.custom.purple[500],
          color: "#fff",
        }
      : {
          backgroundColor: theme.palette.custom.gray[300],
          color: theme.palette.custom.gray[700],
        }),
  });

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 56,
      borderRadius: 2,
    },
  };

  const selectSx = {
    height: 56,
    borderRadius: 2,
  };

  const disabledSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.custom.gray[200],
    },
  };

  return (
    <Box>
      {/* ==============================
          Enviar em
         ============================== */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontSize: 14, mb: 1 }}>Enviar em</Typography>

        <RadioGroup
          value={businessDaysOnly}
          onChange={(e) =>
            updateData({
              businessDaysOnly: e.target.value === "true",
            })
          }
        >
          <FormControlLabel
            disabled={isSingle}
            value={true}
            control={<Radio />}
            label="Somente dias úteis"
          />
          <FormControlLabel
            value={false}
            control={<Radio />}
            label="Todos os dias"
          />
        </RadioGroup>
      </Box>

      {/* ==============================
          Filtros
         ============================== */}
      <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
        Filtros da listagem
      </Typography>

      {isRecurring && (
        <Typography
          sx={{
            fontSize: 14,
            color: theme.palette.custom.gray[600],
            mb: 3,
          }}
        >
          Em campanhas recorrentes, você precisa configurar uma regra por data.
        </Typography>
      )}

      {/* DateRuleSection */}
      <DateRuleSection
        selectedColumn={selectedDueDateColumn}
        hasColumn={hasDueDateColumn}
        daysOffset={daysOffset}
        reference={reference}
        updateData={updateData}
        openColumnModal={openColumnModal}
        buttonSx={(selected) => buttonSx(selected, dateRequiredError)}
        inputSx={inputSx}
        selectSx={selectSx}
        disabledSx={disabledSx}
        showErrors={showErrors}
        isIncomplete={dateIsIncomplete}
        needsDays={dateNeedsDays}
        isRecurring={isRecurring}
        requiredError={dateRequiredError}
        loadingColumns={loadingColumns} 
      />

      {/* ConditionRuleSection */}
      <ConditionRuleSection
        selectedColumn={selectedRuleColumn}
        hasColumn={hasRuleColumn}
        operator={operator}
        value={value}
        updateData={updateData}
        openColumnModal={openColumnModal}
        buttonSx={(selected) => buttonSx(selected, false)}
        inputSx={inputSx}
        selectSx={selectSx}
        disabledSx={disabledSx}
        showErrors={showErrors}
        isIncomplete={conditionIsIncomplete}
        operatorNeedsValue={operatorNeedsValue}
        loadingColumns={loadingColumns}  
      />

      {/* Modal */}
      <ColumnSelectModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        columns={safeColumnOptions}
        onSelect={handleSelectColumn}
        loadingColumns={loadingColumns}
        title={
          columnsError
            ? "Erro ao carregar colunas"
            : loadingColumns
            ? "Carregando colunas..."
            : "Selecione uma coluna"
        }
        selectedColumnId={
          modalField === "dueDate" ? dueDateColumnId : ruleColumnId
        }
      />
    </Box>
  );
}
